import TitleLayout from '@/components/Layout/TitleLayout';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import {
  replaceGuidelineTemplateConditionsBatch,
  useUpsertGuidelineTemplate,
} from '../api/guideline-template';
import GuidelineTemplateConditionList from '../components/GuidelineTemplateConditionList';
import { useHydrateGuidelineTemplate } from './ManageGuidelineTemplate.hooks';

const schema = yup.object().shape({
  name: yup.string().required(),
  diseaseConditions: yup.array().of(
    yup.object().shape({
      cancerTypeId: yup.string().required(),
      cancerPathologyId: yup.string().nullable(),
      cancerLocationId: yup.string().nullable(),
    }),
  ),
});

export type GuidelineTemplateFormValues = {
  name: string;
  diseaseConditions: {
    cancerTypeId?: string;
    cancerPathologyId?: string;
    cancerLocationId?: string;
  }[];
};

const defaultValues: GuidelineTemplateFormValues = {
  name: '',
  diseaseConditions: [
    {
      cancerTypeId: undefined,
      cancerPathologyId: undefined,
      cancerLocationId: undefined,
    },
  ],
};

export default function ManageGuidelineTemplate() {
  const formStore = useFormStore(defaultValues, schema);
  const [updateStore, isValid] = formStore((x) => [x.updateStore, x.isValid]);
  const { templateId, cancerConfigs } = useHydrateGuidelineTemplate(updateStore);
  const upserGuidelineTemplate = useUpsertGuidelineTemplate(templateId);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const onSave = () => {
    const store = formStore.getState();

    if (!store.isValid) return;
    setSaving(true);

    const formData = store.getValues();

    upserGuidelineTemplate
      .mutateAsync({
        name: formData.name,
        diseaseConditions: [],
      })
      .then(async (data) => {
        await replaceGuidelineTemplateConditionsBatch(templateId || data.id, {
          diseaseConditions: formData.diseaseConditions.map((condition, index) => ({
            ...condition,
            sortOrder: index,
          })),
        });
        return data;
      })
      .then(() => {
        setSaving(false);
        navigate('/behavior-config/guidelines/template');
      })
      .catch((error) => {
        setSaving(false);
        console.error(error);
      });
  };
  return (
    <TitleLayout title={`${templateId ? 'Edit' : 'Add'} Condition Template`}>
      <div className="flex flex-col flex-grow overflow-y-auto bg-white rounded-lg shadow-lg max-w-7xl">
        <div className="grid gap-6 px-4 pt-4 flex-grow-1">
          <FormField name="name" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="template.name"
                type="text"
                htmlName="name"
                label="Name"
                error={errorMessage}
                value={value}
                onChange={onChange}
                testSelector="template-name"
                required
              />
            )}
          </FormField>
          {cancerConfigs && (
            <GuidelineTemplateConditionList cancerConfigs={cancerConfigs} formStore={formStore} />
          )}
        </div>
        <div className="flex items-center w-full px-4 py-4 min-h-fit">
          <Button
            className="w-auto px-6 ml-auto mr-3"
            buttonColor="gray"
            onClick={() => navigate(-1)}>
            Back
          </Button>
          <ButtonWithLoading
            loading={saving}
            className="w-auto px-6"
            buttonColor="blue"
            onClick={onSave}
            disabled={!isValid}
            testSelector="save-template">
            Save
          </ButtonWithLoading>
        </div>
      </div>
    </TitleLayout>
  );
}
